import React, { useEffect, useState } from "react";
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import SearchIcon from "@mui/icons-material/Search";
import { deleteDocument, downloadDocumentFile, getDocuments, uploadDocument } from "../../services/Documentservice";
import { withRouter } from "react-router-dom/cjs/react-router-dom";
import { connect } from "react-redux";
import { toastAlert } from "../../helpers/toastAlert";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { Download } from "@mui/icons-material";
import { formatDate, hasPermission } from "../../utils/commonutils";
import ConfirmDeleteModal from "./Confrimationmodal";
import { FilePreviewDrawer } from "./FilePreview";
import MasterSelect from "./MasterSelect";
import { FALLBACK_DOCUMENT_CATEGORY, FALLBACK_DOCUMENT_TYPE } from "../../utils/masterFallbacks";
import { useMasters } from "../../hooks/useMasters";
import {
  ATTACHMENT_ACCEPT,
  validateAttachmentFile,
} from "../../utils/attachmentUtils";

const staticDocuments = [];

const getFileIcon = (type) => {
  if (type.toLowerCase().includes("pdf"))
    return <PictureAsPdfIcon sx={{ color: "#d32f2f", fontSize: 32 }} />;
  if ( 
    type.toLowerCase().includes("image") ||
    type.toLowerCase().includes("jpg") ||
    type.toLowerCase().includes("png")
  )
    return <ImageIcon sx={{ color: "chocolate", fontSize: 32 }} />;
  return <DescriptionIcon sx={{ color: "#1976d2", fontSize: 32 }} />;
};

const DocumentList = (props) => {
  const { id } = useParams();

  const Id = id;
  const [docType, setDocType] = useState("personal");
  const [category, setCategory] = useState("");
  const [tab, setTab] = useState(0);
  const [allDocuments, setAllDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const typeOptions = useMasters("document_type", FALLBACK_DOCUMENT_TYPE);
  const FetchDocuments = async () => {
    try {
      setFetchLoading(true);

      const Res = await getDocuments(props, Id);
      const docs = Res?.data?.documents || [];

      setAllDocuments(docs);
    } catch (error) {
      toastAlert("Unable to fetch documents");
    } finally {
      setFetchLoading(false);
    }
  };
  const handleDeleteDocument = async () => {
    try {
      setDeleteLoading(true);

      await deleteDocument(props, selectedDocumentId);

      toastAlert("Document deleted successfully", "success");

      setDeleteModalOpen(false);
      setSelectedDocumentId(null);

      await FetchDocuments();
    } catch (error) {
      toastAlert("Something went wrong");
    } finally {
      setDeleteLoading(false);
    }
  };
  useEffect(() => {
    FetchDocuments();
  }, [id]);
  useEffect(() => {
    setFilteredDocs(allDocuments.filter((d) => d.type === docType));
  }, [docType, allDocuments]);
  const handleTabChange = (e, newValue) => {
    setTab(newValue);
    const nextType = typeOptions[newValue]?.value || "personal";
    setDocType(nextType);
    setFilteredDocs(allDocuments.filter((d) => d.type === nextType));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const validationError = validateAttachmentFile(file);
    if (validationError) {
      toastAlert(validationError, "error");
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };
  const handleDownload = async (doc) => {
    try {
      const token = props?.authData?.accessToken;
      await downloadDocumentFile(doc._id, token, doc.fileName || "document");
      toastAlert("Download started", "success");
    } catch (err) {
      toastAlert(err?.message || "Download failed", "error");
    }
  };
  const handleFileSubmit = async () => {
    if (!selectedFile) return toastAlert("Please select a file");
    const validationError = validateAttachmentFile(selectedFile);
    if (validationError) return toastAlert(validationError, "error");

    try {
      setUploadLoading(true);

      const res = await uploadDocument(
        props,
        selectedFile,
        Id,
        docType,
        category
      );

      if (res?.status === 200 || res?.status === 201) {
        toastAlert("Document uploaded successfully!", "success");

        await FetchDocuments();

        setSelectedFile(null);
        setCategory("");
      } else {
        toastAlert(res?.response?.data?.message || "Upload failed", "error");
      }
    } catch {
      toastAlert("Error uploading file");
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTabs-indicator": {
              height: "4px",
              backgroundColor: "#0b2967",
              borderRadius: "5px",
              transition: "0.3s",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "bold",
              padding: "12px 24px",
              "&:hover": {
                color: "#0b2967",
              },
            },
            "& .Mui-selected": {
              color: "#0b2967 !important",
            },
          }}
        >
          {typeOptions.map((opt) => (
            <Tab key={opt.value} label={opt.label} />
          ))}
        </Tabs>

        <Divider sx={{ mb: 2 }} />
        {hasPermission("create_document") && (
        <Box
          sx={{
            p: 2,
            border: "1px dashed var(--thm-color)",
            borderRadius: 2,
            mb: 4,
            boxShadow: "0 1px 4px rgba(11, 41, 103, 0.1)",
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: "#43464b",
              mb: 2,
            }}>Upload Document</Typography>

          <Box

          >
            <Grid container spacing={2}>
              {/* Category */}
              <Grid item xs={12} md={6}>
                <MasterSelect
                  group="document_category"
                  fallback={FALLBACK_DOCUMENT_CATEGORY}
                  value={category}
                  placeholder="Category (optional)"
                  onChange={(e) => setCategory(e.target.value)}
                />
              </Grid>


              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{
                    borderColor: "var(--thm-color)",
                    color: "var(--thm-color)",
                    width: "100%",
                    fontWeight: 500,
                    padding: 1.5,
                  }}
                >
                  <UploadFileIcon /> &nbsp;Choose File
                  <input
                    type="file"
                    hidden
                    accept={ATTACHMENT_ACCEPT}
                    onChange={handleFileChange}
                  />
                </Button>
              </Grid>

              {selectedFile && (
                <>
                  {/* Selected File Name */}
                  <Grid item xs={8}>
                    <Typography
                      sx={{
                        bgcolor: "#f5f5f5",
                        p: 1.5,
                        borderRadius: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={4}>
                    <Button
                      variant="contained"
                      onClick={handleFileSubmit}
                      disabled={uploadLoading}
                      sx={{
                        width: "100%",
                        height: "100%",
                        bgcolor: "var(--thm-color)",
                      }}
                    >
                      {uploadLoading ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        "Upload"
                      )}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </Box>
        )}

        {/* Search + List */}
        <Box
          display="flex"
          justifyContent="space-between"
          mb={2}
          alignItems="center"
        >
          <Typography sx={{ fontWeight: 500, fontSize: "20px" }}>
            {typeOptions[tab]?.label || "Documents"}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2, mt: 2 }} />
        <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
          {fetchLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={5}
            >
              <CircularProgress />
            </Box>
          ) : filteredDocs?.length === 0 ? (
            <Typography align="center" color="text.secondary">
              No documents found.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {filteredDocs?.map((doc, index) => (
                <Grid item xs={12} md={12} key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      backgroundColor: "#fff",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    }}
                 
                  >
                    {getFileIcon(doc?.fileType)}

                    <Box flex={1}>
                     <Typography
  variant="subtitle1"
  sx={{
    color: "var(--thm-color)",
    cursor: "pointer",
    fontWeight: 600,
    display: "inline-block",
    "&:hover": {
      color: "var(--thm-color)",
    },
  }}
     onClick={() => {
                      setPreviewDoc(doc);
                    }}
>
  {doc.fileName}
</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Type: {doc.type} |{" "}
                        {doc.category && (
                          <span>Category: {doc.category || "-"}</span>
                        )}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(doc.createdAt)}
                      </Typography>

                      <Tooltip title="Download">
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(doc);
                          }}
                        >
                          <CloudDownloadIcon />
                        </IconButton>
                      </Tooltip>

                      {hasPermission("delete_document") && (
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          disabled={deleteLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDocumentId(doc._id);
                            setDeleteModalOpen(true);
                          }}
                        >
                          {deleteLoading && selectedDocumentId === doc._id ? (
                            <CircularProgress size={20} color="error" />
                          ) : (
                            <DeleteIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </CardContent>
      <ConfirmDeleteModal
        open={deleteModalOpen}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteModalOpen(false);
            setSelectedDocumentId(null);
          }
        }}
        onConfirm={handleDeleteDocument}
        loading={deleteLoading}
        message="Are you sure you want to delete this document? This action cannot be undone."
      />
      <FilePreviewDrawer document={previewDoc} onClose={() => setPreviewDoc(null)} />
    </Card>
  );
};

const mapStateToProps = (state) => ({
  authData: state.authReducer,
});

export default withRouter(connect(mapStateToProps)(DocumentList));